import { localized, msg, str } from "@lit/localize";
import { publish } from "@whilecat/core/events/event-service.js";
import { ANCHOR_SELECTED, LEVEL_ACTION_REQUIRED } from "@whilecat/core/events/task-events.js";
import { LitElement, css, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import "@whilecat/core/tasks/task-structure.ts";
import "@whilecat/core/tasks/common/quiz-task.ts";
import "@whilecat/core/tasks/common/complete-sentence-task.ts";
import "@whilecat/core/tasks/common/text-block-task.ts";
import "@whilecat/core/tasks/java/mark-lines-task.ts";
import "@whilecat/core/tasks/java/code-editor-task.ts";
import WhilecatStyle from "@whilecat/core/whilecat-style.js";

export interface Action {
    id: string;
    name: string;
}

@localized()
@customElement("level-navigation")
export class LevelNavigation extends LitElement {
    static styles = [
        WhilecatStyle,
        css`
          :host {
            display: block;
            color: var(--sl-color-gray-500);
          }

          .navigation-panel {
            width: 13vw;
            position: sticky;
            top: var(--whilecat-page-header-magin);
            margin-right: 100px;
            margin-left: 50px;

            display: flex;
            padding: 32px 24px 24px 24px;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: 8px;

            border-radius: 24px;
            border-right: 4px solid var(--whilecat-card-background-color);
            border-left: 4px solid var(--whilecat-card-background-color);
            background: var(--whilecat-card-background-color);
            backdrop-filter: blur(10px);
          }

          .navigation-panel:hover {
            border: 4px solid var(--whilecat-primary);
            border-top: none;
            border-bottom: none;
          }

          .progress-ring, .progress-ring-completed {
            --track-width: 6px;
            --size: 100px;
          }

          .progress-ring {
            --indicator-color: var(--whilecat-primary);
          }

          .progress-ring-completed {
            --indicator-color: var(--whilecat-success-color-3);
            animation: waveAnimation 1s;
          }

          @keyframes waveAnimation {
            0% {
              border-radius: 100px;
              box-shadow: 0 0 0 0 var(--whilecat-success-color-3)
            }
            50% {
              border-radius: 100px;
              box-shadow: 0 0 10px 5px var(--whilecat-success-color-3)
            }
            100% {
              box-shadow: 0 0 10px 10px transparent;
            }
          }

          sl-divider {
            width: 100%;
            margin-bottom: 0;
          }

          sl-button.anchor::part(base) {
            color: var(--whilecat-light-1)
          }

          sl-button.anchor::part(base):hover {
            color: var(--whilecat-light-3)
          }

          /* So that long anchors are broken into multiple lines, respecting the max width. */
          sl-button.anchor::part(label) {
            white-space: normal;
            text-align: left;
          }

          sl-button.anchor-last::part(base) {
            color: var(--whilecat-light-3);
          }

          sl-button.anchor-last::part(base):hover {
            color: var(--whilecat-light-4);
          }
        `,
    ];

    @property({ attribute: false }) actions: Action[] = [];
    @property({ attribute: false }) navigationAnchors!: string[];
    @property({ attribute: false }) completedCount!: number;
    @property({ attribute: false }) totalCount!: number;

    getPercent() {
        if (this.totalCount === 0) return 0;
        return Math.round((this.completedCount / this.totalCount) * 100);
    }

    goToAnchor(anchor: string) {
        publish(ANCHOR_SELECTED, anchor);
    }

    sendActionEvent(action: Action) {
        publish(LEVEL_ACTION_REQUIRED, action);
    }

    getProgressRingClass() {
        return !this.isCompleted() ? "progress-ring" : "progress-ring-completed";
    }

    isCompleted() {
        return this.completedCount >= this.totalCount;
    }

    getProgressRingInnerText() {
        if (this.isCompleted()) {
            return html`
              <sl-icon name="check-lg" style="color: var(--whilecat-success-color-3); font-size: 32px;">
              </sl-icon>
            `;
        }

        return html`
          ${msg(str`${this.completedCount} of ${this.totalCount}`)}
        `;
    }

    getAnchors() {
        if (this.navigationAnchors.length <= 0) {
            return nothing;
        }
        const anchorElements = this.navigationAnchors.map((anchor, index) => {
            const extraClass = index < this.navigationAnchors.length - 1 ? "" : "anchor-last";

            return html`
              <div>
                <sl-button class="anchor ${extraClass}"
                           variant="text"
                           size="small"
                           @click=${() => this.goToAnchor(anchor)}>
                  - ${anchor}
                </sl-button>
              </div>
            `;
        });

        return html`
          <div class="anchors">
            ${anchorElements}
          </div>
        `;
    }

    getActions() {
        return this.actions.length > 0
            ? html`
          <div class="actions">
            ${this.actions.map(
                action => html`
                  <sl-button class="action" variant="text" @click=${() => this.sendActionEvent(action)}>
                    ${action.name}
                  </sl-button>
            `,
            )}
          </div>
        `
            : nothing;
    }

    render() {
        return html`
          <div class="navigation-panel">
              <sl-progress-ring value="${this.getPercent()}" class="${this.getProgressRingClass()}">
                ${this.getProgressRingInnerText()}
              </sl-progress-ring>
            <sl-divider class="divider"></sl-divider>
            ${this.getAnchors()}
            ${this.getActions()}
          </div>
        `;
    }
}
